import React from 'react';
import { connect } from 'react-redux';
import { ToxnotComponentProperties } from './core/BaseApplicationComponents/ToxnotComponent';
import { ApplicationState } from '../store';
import { AppServices, TowState } from '../AppServices';
import * as LoginStateController from '../store/StateControllers/LoginStateController';
import { ToxnotReactComponentPaths } from './core/ToxnotReactComponentPaths';
import { ToxnotSignInRequiredComponent } from './core/BaseApplicationComponents/ToxnotSignInRequiredComponent';

// TODO: MJM SM2 merge with WorkflowType in TaskOrientedWorkflowFactory.ts 
enum WorkflowType {
    ImportData = "ImportData",
    ImportDataToExistingInventoryProduct = "ImportDataToExistingInventoryProduct",
    ImportDataToExistingInventoryMaterial = "ImportDataToExistingInventoryMaterial",
    Declare = "Declare",
    DeclareRenew = "DeclareRenew",
    HPD = "HPD",
    ComplianceReport = "ComplianceReport",
    PublishSM = "PublishSM",
    PublishAveryDennisonCompliance = "PublishAveryDennisonCompliance"
}

interface TempPublishTowSimpleContext {
    "tailorSharedMaterial": string;
}

export interface TempPublishTowState {
    activeStep: string,
    singleResultInventoryId: string;
    multiResultInventoryIds: string[];
    importMode: string;
    importId: string;
    inception: string;
    publicInventoryId: string;
    publicInventoryDataId: string;
    implementationInventoryId: string;
    sourceInventoryTargetVersionId: string;
    sharedMaterialId: string;
    context: TempPublishTowSimpleContext;
    previewStartId: string | null;
    previewStartContext: string | null;
    accessEmails: string;
}

type TowProps =
    ToxnotComponentProperties &
    typeof LoginStateController.actionCreators;

class TOW extends ToxnotSignInRequiredComponent<TowProps> {
    // ===============================================================================

    protected override getDisallowedRoles() {
        return [];
    }

    // TODO: MJM SM2 merge with withTaskOrientedWorkflowFactory.ts
    private readonly localizedTOWNames: { [key: string]: string } = {
        [WorkflowType.ImportData]: this.localizeString("Import Data"),
        [WorkflowType.ImportDataToExistingInventoryProduct]: this.localizeString("Import Data"),
        [WorkflowType.ImportDataToExistingInventoryMaterial]: this.localizeString("Import Data"),
        [WorkflowType.Declare]: this.localizeString("Submit Declare Label"),
        [WorkflowType.DeclareRenew]: this.localizeString("Renew Declare Label"),
        [WorkflowType.HPD]: this.localizeString("Create HPD"),
        [WorkflowType.ComplianceReport]: this.localizeString("Create Compliance Report"),
        [WorkflowType.PublishSM]: this.localizeString("Publish Passport"),
        [WorkflowType.PublishAveryDennisonCompliance]: this.localizeString("Publish Compliance Letter")
    };

    protected renderConcreteComponent() {
        const towState = this.props.appServices.towState;
        if (null === towState) {
            return (
                <div id="tow-launchers-container">
                </div>
                );
        }

        return (
            <React.Fragment>
                <svg style={{display: "none"}}>
                    <symbol id="step-active-neither">
                        <polyline points="0,0 9,12 0,25" style={{fill:"#fff", stroke:"rgba(0,0,0,0.1)"}}></polyline>
                    </symbol>
                    <symbol id="step-active-left">
                        <polygon points="0,0 9,12 0,25" style={{ fill:"#42af3d"}}></polygon>
                        <polyline points="0,0 9,12 0,25" style={{ fill:"#42af3d", stroke:"rgba(0,0,0,0.1)"}}></polyline>
                    </symbol>
                    <symbol id="step-active-right">
                        <polygon points="0,0 9,12 0,25 10,25 10,0" style={{ fill:"#42af3d"}}></polygon>
                        <polyline points="0,0 9,12 0,25" style={{ fill: "#fff", stroke:"#42af3d"}}></polyline>
                    </symbol>
                </svg>
                <div id="tow-active-workflow-container">
                    <div className="tow-workflow-content">
                        <div className="tow-workflow-nav">
                            <div className="tow-title">
                                {this.localizedTOWNames[towState.type]}
                            </div>
                            <div className="tow-sequence-diagram">
                                {this.isSpecialStep(towState) ? this.renderActive() : this.renderResume()}
                            </div>
                            <div className="tow-nav-actions">
                                <button type="button" id="tow-cancel-button" className="btn" onClick={this.close_onClick}><span className="glyphicon glyphicon-remove"></span></button>
                            </div>
                        </div>
                    </div>
                </div>
            </React.Fragment>
        );
    }

    // ===============================================================================

    private isSpecialStep(towState: TowState) {
        if (!location.href.includes(ToxnotReactComponentPaths.SharedMaterialPublishPreview) || towState.type !== WorkflowType.PublishSM) {
            return false;
        }

        const state = JSON.parse(towState.jsonState) as TempPublishTowState;
        return state.activeStep === "PreviewShallow";
    }

    // ===============================================================================

    private renderResume() {
        return (
            <div className="tow-sequence-diagram">
                <button type="button" className="btn btn-sm btn-toxnot-tow-nav-button tow-resume-button" onClick={this.resume_onClick}>
                    {this.localizeString("Resume")} <span className="glyphicon glyphicon-play"></span>
                </button>
            </div>
        );
    }

    // ===============================================================================   

    private renderInactiveStep(localizedName: string, stepKey: string, activeRight: boolean, afterActiveStep: boolean, lastStep: boolean) {
        return (
            <div className="tow-tab-step" title="Navigate to this step">
                <div className="tow-step-body" data-key={stepKey} style={{ cursor: "pointer" }} onClick={afterActiveStep ? this.nextStep_onClick : this.jumpToStep_onClick}>
                    {localizedName}
                </div>
                {lastStep ?
                    <div className="tow-step-end-cap"></div> :
                    <div className="tow-step-join">
                        <svg style={{ width: "10px", height: "100%" }}><use href={activeRight ? "#step-active-right" : "#step-active-neither"}></use></svg>
                    </div>}
            </div>
        );
    }

    private renderActiveStep() {
        return (<div className="tow-tab-step" title="The current step of the Workflow"><div className="tow-step-body tow-step-active" data-key="PreviewShallow" id="tow-step-active">Digital Product Passport Preview</div><div className="tow-step-join"><svg style={{ width: "10px", height: "100%" }}><use href="#step-active-left"></use></svg></div></div>);
    }

    private renderActive() {
        const towState = this.props.appServices.towState!;
        const state = JSON.parse(towState.jsonState) as TempPublishTowState;

        return (
            <React.Fragment>
                <button type="button" id="tow-bump-previous-button" className="btn btn-sm btn-toxnot-tow-nav-button tow-bump-button"
                    title="Navigate to the previous step" onClick={this.previousStep_onClick}>
                    <span className="glyphicon glyphicon-chevron-left"></span>
                </button>
                {state.inception === "LibrarySelectSingle" && this.renderInactiveStep("Select Products/Materials", "LibrarySingleSelect", false, false, false)}
                {this.renderInactiveStep(this.localizeString("Review"), "ReviewBOM", false, false, false)}
                {this.renderInactiveStep(this.localizeString("Customize"), "Tailor", true, false, false)}
                {this.renderActiveStep()}
                {this.renderInactiveStep(this.localizeString("Passport Preview"), "PreviewDeep", false, true, false)}
                {this.renderInactiveStep(this.localizeString("Publish"), "Publish", false, true, true)}
                <button type="button" id="tow-bump-next-button" className="btn btn-sm btn-toxnot-tow-nav-button tow-bump-button"
                    title="Navigate to the next step" onClick={this.nextStep_onClick}>
                    <span className="glyphicon glyphicon-chevron-right"></span>
                </button>
            </React.Fragment>
        );
    }

    // ===============================================================================

    private resume_onClick = (ev: React.MouseEvent) => {
        this.props.towResume();
    }

    // ===============================================================================

    private jumpToStep(step: string) {
        const towState = this.props.appServices.towState!;
        const state = JSON.parse(towState.jsonState) as TempPublishTowState;
        state.activeStep = step;
        state.previewStartId = null;
        state.previewStartContext = null;
        towState.jsonState = JSON.stringify(state);
        this.props.towTaskUpdate(towState);
    }

    // ===============================================================================

    private previousStep_onClick = (ev: React.MouseEvent) => {
        this.jumpToStep("Tailor");
    }

    // ===============================================================================

    private nextStep_onClick = (ev: React.MouseEvent) => {
        this.jumpToStep("PreviewDeep");
    }

    // ===============================================================================

    private jumpToStep_onClick = (ev: React.MouseEvent<HTMLElement>) => {
        this.jumpToStep(ev.currentTarget.getAttribute("data-key")!);
    }

    // ===============================================================================

    private close_onClick = (ev: React.MouseEvent) => {
        $("#tow-decision-modal").modal("show");
    }
}

// ===============================================================================

export default connect(
    (state: ApplicationState) => ({ appServices: state.appServices }),
    LoginStateController.actionCreators
)(TOW as any);
